/**
 * The quote document's colours and geometry, read out of the approved template in
 * `docs/quote-template/`. The backend's `QuotePdfLayout` and `QuotePdfStyles` hold the same numbers
 * for the customer's PDF — the two lists have to be edited together, which is why both name the
 * template as their source. The sheet's fixed structural labels ("Customer Signature", "VAT #:",
 * the column names) are duplicated the same way and for the same reason; every string that depends
 * on the order, and everything Legal owns, arrives resolved from the document endpoint instead.
 *
 * **No colour here comes from the MUI theme, and none may.** The quote document is a printed
 * artefact: it has to read the same to every viewer, on paper, and in either portal palette. Adding
 * a themed colour or an `sx` colour anywhere inside the sheet brings back the defect this file
 * exists to stop, where MUI's `TableCell` took `palette.text.primary` and rendered the whole price
 * table in near-white on white paper in dark mode. `QuoteSheetPalette.spec.tsx` fails if it comes
 * back.
 */
export const quoteDocumentColors = {
  /** The sheet's own paper. */
  paper: "#ffffff",
  /** Body text, at 10pt. */
  ink: "#000000",
  /** The 1pt and 1.5pt table rules. */
  rule: "#000000",
  /** Behind the price table's header row. */
  tableHeader: "#efefef",
  /** The template's link colour. */
  link: "#1155cc",
  /** Only outside the printed area: the sheet's drop shadow on screen. */
  screenShadow: "rgba(0, 0, 0, 0.18)",
} as const;

/**
 * US Letter with the template's 36pt (0.5in) margin, so 540pt of content. Expressed in millimetres
 * because that is what CSS page sizing takes.
 */
export const quoteDocumentLayout = {
  pageWidth: "215.9mm",
  pageMargin: "12.7mm",
  bodyFontSize: "10pt",
  bandFontSize: "9pt",
  /** Under a table whose figures need qualifying. The band's size, rather than a fourth one. */
  caveatFontSize: "9pt",
  subHeadingFontSize: "11pt",
  headingFontSize: "12pt",
  titleFontSize: "30pt",
  lineHeight: 1.15,
  logoHeight: "34pt",
  /** The template's 5pt above and below a table cell's text. */
  cellPadding: "5pt",
  /**
   * The template's gutter to the left and to the right of a table cell's text. Measured off
   * `Quote Template.pdf` page 1: the price table's three cells are drawn at x=36, x=216.75 and
   * x=471.75, and their text starts at x=41.25, x=222 and x=477 — 5.25pt inside each cell. The
   * right-aligned `Total` column ends at x=573 against a cell edge at x=578.25, the same 5.25pt.
   * The header band measures the same on all four of its columns.
   *
   * Without it a full-width value touches the next column's text. The template hides the fault
   * because its sample customer is "Company Name" and its price table has one row.
   *
   * The template's own tables hang into the page margin by this amount so the outer text stays
   * flush with the body text. The sheet does not: its tables span the content box, so the padding
   * insets the outermost text by 5.25pt as well. The PDF pads a cell the same way, and the two
   * surfaces reading alike matters more than 5.25pt at each edge.
   */
  cellSidePadding: "5.25pt",
} as const;

/**
 * The template's column widths as percentages, so the sheet reflows on a narrow screen and still
 * prints to the measured geometry. Each set is the template's own point widths over their total.
 */
export const quoteDocumentColumns = {
  /** Customer, fact label, fact value, seller: 161.25 / 91.5 / 157.5 / 137.2pt. */
  band: ["29.5%", "16.7%", "28.8%", "25.0%"],
  /** Product, Description, Total: 180.8 / 255 / 106.5pt. */
  price: ["33.3%", "47.0%", "19.7%"],
  /** Label, rule, label, rule: 126 / 203.2 / 82.5 / 92.2pt. */
  signature: ["25.0%", "40.3%", "16.4%", "18.3%"],
  /** Label then rule: 127.5 / 378.8pt. */
  customerInformation: ["25.2%", "74.8%"],
} as const;
